import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Loader2, Sparkles } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

function CompanyTab() {
  const [c, setC] = useState(null);
  useEffect(() => { api.get("/company").then(({ data }) => setC(data)); }, []);
  if (!c) return <Loading />;
  const save = async () => { try { await api.put("/company", c); toast.success("法人情報を更新しました"); } catch (e) { toast.error(apiErr(e)); } };
  const fields = [["name", "法人名"], ["corporate_number", "法人番号"], ["zip_code", "郵便番号"], ["address", "住所"], ["phone", "電話番号"], ["email", "メールアドレス"], ["representative", "代表者名"]];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
      <div className="grid sm:grid-cols-2 gap-4">
        {fields.map(([k, l]) => (
          <div key={k}><Label>{l}</Label><Input value={c[k] || ""} onChange={(e) => setC({ ...c, [k]: e.target.value })} className="mt-1" data-testid={`company-${k}`} /></div>
        ))}
      </div>
      <Button className="mt-5 bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="save-company-btn">保存</Button>
    </div>
  );
}

function ServiceTypesTab() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [name, setName] = useState("");
  const load = useCallback(() => {
    api.get("/service-types").then(({ data }) => setItems(data));
    api.get("/service-templates").then(({ data }) => setTemplates(data));
  }, []);
  useEffect(() => { load(); }, [load]);
  const add = async () => { if (!name) return; await api.post("/service-types", { name, order: (items?.length || 0) + 1 }); toast.success("追加しました"); setName(""); setOpen(false); load(); };
  const toggle = async (s) => { await api.put(`/service-types/${s.id}`, { ...s, status: s.status === "有効" ? "停止" : "有効" }); load(); };
  const applyTpl = async (key) => { try { const { data } = await api.post("/service-types/apply-template", { key }); toast.success(`${data.name} を追加（法定項目 ${data.created}件）`); load(); } catch (e) { toast.error(apiErr(e)); } };
  if (!items) return <Loading />;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
      <div className="flex justify-between items-center mb-4">
        <h3 className="font-semibold text-slate-800">サービス種別マスター</h3>
        <div className="flex gap-2">
          <Dialog open={tplOpen} onOpenChange={setTplOpen}>
            <DialogTrigger asChild><Button size="sm" variant="outline" data-testid="tpl-btn"><Sparkles className="w-4 h-4 mr-1" /> テンプレートから追加</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>サービス種別テンプレート</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500">選択すると、そのサービスの法定研修・委員会・訓練セットをまとめて追加します（回数は後で調整可能）。</p>
              <div className="max-h-80 overflow-y-auto divide-y divide-slate-100">
                {templates.map((t) => (
                  <div key={t.key} className="flex items-center justify-between py-2.5" data-testid={`tpl-${t.key}`}>
                    <div><div className="text-sm text-slate-800">{t.name}</div><div className="text-xs text-slate-400">法定項目 {t.item_count}件</div></div>
                    <Button size="sm" disabled={t.already_added} onClick={() => applyTpl(t.key)} className="bg-blue-600 hover:bg-blue-700" data-testid={`apply-tpl-${t.key}`}>{t.already_added ? "追加済" : "追加"}</Button>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-svc-btn"><Plus className="w-4 h-4 mr-1" /> 追加</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>サービス種別を追加</DialogTitle></DialogHeader>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="例：就労移行支援" data-testid="svc-name-input" />
              <DialogFooter><Button onClick={add} className="bg-blue-600 hover:bg-blue-700" data-testid="save-svc-btn">追加</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        {items.map((s) => (
          <div key={s.id} className="flex items-center justify-between py-3">
            <span className="text-sm text-slate-700">{s.name}</span>
            <Button size="sm" variant="outline" onClick={() => toggle(s)} data-testid={`toggle-svc-${s.id}`}>{s.status === "有効" ? "有効" : "停止中"}</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function LegalMasterTab() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => { api.get("/legal-master").then(({ data }) => setItems(data)); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => { await api.put(`/legal-master/${editing.id}`, editing); toast.success("更新しました（法定回数は固定値ではなく変更可能です）"); setEditing(null); load(); };
  if (!items) return <Loading />;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <Table>
        <TableHeader><TableRow><TableHead>名称</TableHead><TableHead>区分</TableHead><TableHead className="text-center">最低実施回数</TableHead><TableHead>法的根拠</TableHead><TableHead></TableHead></TableRow></TableHeader>
        <TableBody>
          {items.map((m) => (
            <TableRow key={m.id} data-testid={`legal-${m.id}`}>
              <TableCell className="font-medium text-slate-800">{m.name}</TableCell>
              <TableCell><span className="text-xs bg-slate-100 px-2 py-0.5 rounded">{m.kind}</span></TableCell>
              <TableCell className="text-center">{m.min_count}回</TableCell>
              <TableCell className="text-xs text-slate-500">{m.legal_basis}</TableCell>
              <TableCell><button onClick={() => setEditing(m)} className="text-slate-400 hover:text-blue-600" data-testid={`edit-legal-${m.id}`}><Pencil className="w-4 h-4" /></button></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>法定マスターを編集</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>名称</Label><Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>最低実施回数</Label><Input type="number" value={editing.min_count} onChange={(e) => setEditing({ ...editing, min_count: Number(e.target.value) })} className="mt-1" data-testid="legal-mincount-input" /></div>
                <div><Label>会社目標回数</Label><Input type="number" value={editing.company_target_count || 0} onChange={(e) => setEditing({ ...editing, company_target_count: Number(e.target.value) })} className="mt-1" /></div>
              </div>
              <div><Label>法的根拠</Label><Input value={editing.legal_basis || ""} onChange={(e) => setEditing({ ...editing, legal_basis: e.target.value })} className="mt-1" /></div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="save-legal-btn">保存</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PasswordTab() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post("/auth/change-password", f); toast.success("パスワードを変更しました"); setF({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-md">
      <div className="space-y-3">
        <div><Label>現在のパスワード</Label><Input type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} className="mt-1" data-testid="cur-pw" /></div>
        <div><Label>新しいパスワード（8文字以上）</Label><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} className="mt-1" data-testid="new-pw" /></div>
      </div>
      <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={save} disabled={busy} data-testid="save-pw-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "変更"}</Button>
    </div>
  );
}

function IntegrationsTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings/integrations").then(({ data }) => setS(data)); }, []);
  const save = async () => { try { await api.put("/settings/integrations", s); toast.success("通知連携設定を保存しました"); } catch (e) { toast.error(apiErr(e)); } };
  if (!s) return <Loading />;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl space-y-6">
      <div className="rounded-lg bg-blue-50 text-blue-800 text-sm p-3 leading-relaxed">
        サイト内メール（受信箱）は常に有効で、差出人は<b>事業所名</b>で表示されます。<br />
        LINE / WhatsApp は<b>無料API</b>のトークンを接続すると有効化されます（未接続時はサイト内メールのみ配信）。
      </div>
      <div>
        <div className="flex items-center justify-between">
          <div><div className="font-medium text-slate-800">LINE通知</div><div className="text-xs text-slate-400">LINE公式アカウント Messaging API（無料枠）</div></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={s.line_enabled} onCheckedChange={(c) => setS({ ...s, line_enabled: !!c })} data-testid="line-enable" /> 有効化</label>
        </div>
        <Label className="mt-3 block">チャネルアクセストークン</Label>
        <Input value={s.line_token || ""} onChange={(e) => setS({ ...s, line_token: e.target.value })} placeholder="LINE Developersで無料発行したトークン" className="mt-1" data-testid="line-token" />
      </div>
      <div className="border-t pt-4">
        <div className="flex items-center justify-between">
          <div><div className="font-medium text-slate-800">WhatsApp通知</div><div className="text-xs text-slate-400">Meta WhatsApp Cloud API（無料枠）</div></div>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={s.whatsapp_enabled} onCheckedChange={(c) => setS({ ...s, whatsapp_enabled: !!c })} data-testid="wa-enable" /> 有効化</label>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div><Label>アクセストークン</Label><Input value={s.whatsapp_token || ""} onChange={(e) => setS({ ...s, whatsapp_token: e.target.value })} className="mt-1" data-testid="wa-token" /></div>
          <div><Label>Phone Number ID</Label><Input value={s.whatsapp_phone_id || ""} onChange={(e) => setS({ ...s, whatsapp_phone_id: e.target.value })} className="mt-1" data-testid="wa-phone" /></div>
        </div>
      </div>
      <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="save-int-btn">保存</Button>
    </div>
  );
}

function ReminderTab() {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/reminders/run"); setRes(data.processed); toast.success(`${data.processed}件のリマインドを配信しました`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl">
      <h3 className="font-semibold text-slate-800 mb-2">受講リマインド（自動）</h3>
      <p className="text-sm text-slate-500 leading-relaxed">毎朝8時（JST）に自動でチェックし、受講期限の <b>7日前・3日前・当日・超過</b> のタイミングで、対象職員本人と事業所管理者へ配信します（サイト内メール＋有効化済みのLINE/WhatsApp）。</p>
      <ul className="text-sm text-slate-600 mt-3 list-disc pl-5 space-y-1">
        <li>7日前：受講のお願い</li><li>3日前：期限が近い旨</li><li>当日：本日が締切</li><li>超過：未受講の督促（毎日）</li>
      </ul>
      <Button className="mt-4 bg-blue-600 hover:bg-blue-700" onClick={run} disabled={busy} data-testid="run-reminder-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "今すぐ送信（テスト）"}</Button>
      {res != null && <div className="text-sm text-slate-500 mt-2" data-testid="reminder-result">配信件数：{res}件</div>}
    </div>
  );
}

function ReminderTemplateTab() {
  const [offices, setOffices] = useState([]);
  const [officeId, setOfficeId] = useState("__all__");
  const [tpl, setTpl] = useState(null);

  useEffect(() => { api.get("/offices").then(({ data }) => setOffices(data)); }, []);
  const load = useCallback((oid) => {
    const q = oid === "__all__" ? "" : `?office_id=${oid}`;
    api.get(`/settings/reminder-template${q}`).then(({ data }) => setTpl(data));
  }, []);
  useEffect(() => { load(officeId); }, [officeId, load]);

  const save = async () => {
    try {
      await api.put("/settings/reminder-template", {
        office_id: officeId === "__all__" ? null : officeId,
        subject: tpl.subject, body: tpl.body,
      });
      toast.success("リマインド文面を保存しました");
      load(officeId);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-2xl space-y-4">
      <div className="rounded-lg bg-blue-50 text-blue-800 text-sm p-3 leading-relaxed">
        受講リマインドの件名・本文を編集できます。事業所ごとに個別設定でき、未設定の事業所には<b>全社共通</b>の文面が使われます。<br />
        差込タグ：<code className="bg-white px-1 rounded">{"{name}"}</code>（氏名）
        <code className="bg-white px-1 rounded ml-1">{"{training}"}</code>（研修名）
        <code className="bg-white px-1 rounded ml-1">{"{label}"}</code>（期限の状況文）
        <code className="bg-white px-1 rounded ml-1">{"{due}"}</code>（期限日）
        <code className="bg-white px-1 rounded ml-1">{"{days}"}</code>（日数）
      </div>
      <div>
        <Label>対象</Label>
        <Select value={officeId} onValueChange={setOfficeId}>
          <SelectTrigger className="mt-1 max-w-xs" data-testid="rtpl-office-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">全社共通</SelectItem>
            {offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {tpl && (
        <>
          <div><Label>件名</Label><Input value={tpl.subject} onChange={(e) => setTpl({ ...tpl, subject: e.target.value })} className="mt-1" data-testid="rtpl-subject" /></div>
          <div><Label>本文</Label><Textarea value={tpl.body} onChange={(e) => setTpl({ ...tpl, body: e.target.value })} className="mt-1" rows={6} data-testid="rtpl-body" /></div>
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="save-rtpl-btn">保存</Button>
        </>
      )}
    </div>
  );
}

export default function Settings() {
  return (
    <div>
      <PageHeader title="設定" subtitle="法人情報・マスター・アカウント設定" />
      <Tabs defaultValue="company">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="company" data-testid="tab-company">法人情報</TabsTrigger>
          <TabsTrigger value="service" data-testid="tab-service">サービス種別</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal">法定マスター</TabsTrigger>
          <TabsTrigger value="notify" data-testid="tab-notify">通知連携</TabsTrigger>
          <TabsTrigger value="reminder" data-testid="tab-reminder">リマインド</TabsTrigger>
          <TabsTrigger value="rtemplate" data-testid="tab-rtemplate">リマインド文面</TabsTrigger>
          <TabsTrigger value="password" data-testid="tab-password">パスワード</TabsTrigger>
        </TabsList>
        <TabsContent value="company"><CompanyTab /></TabsContent>
        <TabsContent value="service"><ServiceTypesTab /></TabsContent>
        <TabsContent value="legal"><LegalMasterTab /></TabsContent>
        <TabsContent value="notify"><IntegrationsTab /></TabsContent>
        <TabsContent value="reminder"><ReminderTab /></TabsContent>
        <TabsContent value="rtemplate"><ReminderTemplateTab /></TabsContent>
        <TabsContent value="password"><PasswordTab /></TabsContent>
      </Tabs>
    </div>
  );
}
